import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { PageShell, PageHero } from "@/components/PageShell";
import { supabase } from "@/integrations/supabase/client";

const newsQueryOptions = (lang: string) =>
  queryOptions({
    queryKey: ["news", "list", lang],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("news_articles")
        .select("id, slug, title, excerpt, cover_image_url, published_at, language")
        .eq("published", true)
        .eq("language", lang)
        .order("published_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 60_000,
  });

export const Route = createFileRoute("/news")({
  head: () => ({
    meta: [
      { title: "News — Comm42 SA" },
      { name: "description", content: "News, insights and editorial updates from Comm42 — strategy, investigation, legal protection and international logistics." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://comm42.ch/news" },
      { property: "og:title", content: "News — Comm42 SA" },
      { property: "og:description", content: "News, insights and editorial updates from Comm42." },
    ],
    links: [{ rel: "canonical", href: "https://comm42.ch/news" }],
  }),
  loader: ({ context }) => {
    context.queryClient.ensureQueryData(newsQueryOptions("en"));
  },
  component: NewsPage,
});

function NewsPage() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").startsWith("it") ? "it" : "en";

  const { data: articles, isLoading, error } = useQuery(newsQueryOptions(lang));

  return (
    <PageShell>
      <PageHero
        eyebrow={t("news.eyebrow")}
        title={t("news.title")}
        subtitle={t("news.subtitle")}
      />
      <section className="mx-auto max-w-6xl px-6 py-20">
        {isLoading ? (
          <p className="text-center text-muted-foreground">{t("news.loading")}</p>
        ) : error ? (
          <p className="text-center text-muted-foreground">{t("news.empty")}</p>
        ) : !articles || articles.length === 0 ? (
          <p className="text-center text-muted-foreground">{t("news.empty")}</p>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {articles.map((a) => (
              <Link
                key={a.id}
                to="/news/$slug"
                params={{ slug: a.slug }}
                className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card transition-colors hover:border-primary"
              >
                {a.cover_image_url && (
                  <img
                    src={a.cover_image_url}
                    alt={a.title}
                    className="aspect-video w-full object-cover"
                    loading="lazy"
                  />
                )}
                <div className="flex flex-1 flex-col p-6">
                  {a.published_at && (
                    <time className="text-xs uppercase tracking-widest text-muted-foreground">
                      {new Date(a.published_at).toLocaleDateString(lang === "it" ? "it-CH" : "en-GB", { day: "numeric", month: "long", year: "numeric" })}
                    </time>
                  )}
                  <h2 className="mt-3 font-serif text-2xl text-foreground group-hover:text-primary transition-colors">
                    {a.title}
                  </h2>
                  {a.excerpt && (
                    <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{a.excerpt}</p>
                  )}
                  <span
                    aria-label={`${lang === "it" ? "Leggi l'articolo" : "Read the article"}: ${a.title}`}
                    className="mt-4 inline-block text-xs uppercase tracking-widest text-primary group-hover:underline"
                  >
                    {lang === "it" ? "Leggi l'articolo" : "Read the article"} →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </PageShell>
  );
}